import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { KeyRound, KeySquare, Radio, ShieldCheck, Scissors, Wrench, ArrowUpRight, DoorOpen } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "./smooth";
import { useT } from "@/lib/i18n";

const ICONS = [KeyRound, KeySquare, Radio, ShieldCheck, Scissors, Wrench, DoorOpen];
const TXT = {
  no: {
    items: [
      ["Ekstra nøkkel", "Kopi av eksisterende nøkkel – programmert og klar til bruk."],
      ["Alle bilnøkler mistet", "Ny nøkkel og programmering selv om du har mistet alle nøklene til bilen."],
      ["Programmering av bilnøkler", "Programmering av bilnøkkel, fjernkontroll, smartnøkler og keyless go."],
      ["Startsperre & chipping", "Programmering av transponderbrikker, feilsøking og koding av startsperre."],
      ["Nøkkelfresing", "Fresing av nøkkelblad etter kode eller original nøkkel."],
      ["Nytt nøkkelskall", "Slitt eller ødelagt nøkkel? Du får nytt skall med nye knapper."],
      ["Bilåpning", "Låst deg ute av bilen? Ta kontakt, så hjelper vi deg inn."],
    ],
    eyebrow: "Bilnøkler", h2a: "Alt om nøkler.", h2b: "Ett sted.",
    p: "Trenger du programmering av bilnøkkel eller bilåpning i Stavanger? Fra ekstranøkkel til startsperre – vi freser, programmerer og koder nøkler til de fleste bilmerker.",
    alt: "Bilnøkkel med fjernkontroll", more: "Les mer om bilnøkler og se priser", big: "Nøkkel", tags: "Transponder · Fjernkontroll · Keyless", cta: "Trenger du ny nøkkel? Spør oss.",
  },
  en: {
    items: [
      ["Spare key", "A copy of your existing key – programmed and ready to use."],
      ["All car keys lost", "A new key and programming, even if you have lost every key to the car."],
      ["Car key programming", "Programming of car keys, remotes, smart keys and keyless go."],
      ["Immobiliser & chipping", "Transponder chip programming, troubleshooting and immobiliser coding."],
      ["Key cutting", "Cutting of key blades by code or from the original key."],
      ["New key shell", "Worn or broken key? You get a new shell with new buttons."],
      ["Car opening", "Locked yourself out of the car? Get in touch and we will help you get in."],
    ],
    eyebrow: "Car keys", h2a: "Everything keys.", h2b: "One place.",
    p: "Need car key programming or a car opening in Stavanger? From spare keys to immobilisers – we cut, program and code keys for most car brands.",
    alt: "Car key with remote control", more: "Read more about car keys and see prices", big: "Key", tags: "Transponder · Remote · Keyless", cta: "Need a new key? Ask us.",
  },
};

export const Keys = () => {
  const t = useT(TXT);
  const ITEMS = t.items.map(([title, d], i) => ({ icon: ICONS[i], t: title, d }));
  return (
  <section id="bilnokler" data-testid="keys-section" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 md:py-36">
    <Reveal className="mb-14 grid gap-6 md:grid-cols-12 md:items-end">
      <div className="md:col-span-7">
        <Eyebrow index="04">{t.eyebrow}</Eyebrow>
        <h2 className="font-display text-5xl font-black uppercase leading-[0.9] md:text-7xl">{t.h2a}<br /><span className="text-zinc-400">{t.h2b}</span></h2>
      </div>
      <div className="md:col-span-5">
        <p className="text-base leading-relaxed text-zinc-300">{t.p}</p>
        <Link to="/bilnokler" data-testid="keys-page-link" className="mt-5 inline-flex items-center gap-2 text-sm text-white underline decoration-[var(--red)] underline-offset-4 hover:text-zinc-200">
          {t.more} <ArrowUpRight size={14} />
        </Link>
      </div>
    </Reveal>

    <div className="grid gap-4 lg:grid-cols-12">
      <Reveal className="lg:col-span-5">
        <div className="clip-notch relative h-80 overflow-hidden border border-white/10 sm:h-[28rem] lg:h-full lg:min-h-[36rem]">
          <img src="/img/keys.webp" loading="lazy" alt={t.alt} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#08090b] via-transparent to-transparent" />
          <div className="absolute bottom-6 left-6 right-6">
            <div className="font-display text-6xl font-black uppercase leading-none md:text-7xl">{t.big}<span className="text-[var(--red)]">.</span></div>
            <div className="mt-2 font-mono text-xs uppercase tracking-widest text-zinc-300">{t.tags}</div>
          </div>
        </div>
      </Reveal>
      <div className="grid gap-4 sm:grid-cols-2 lg:col-span-7">
        {ITEMS.map((it, i) => (
          <Reveal key={i} delay={(i % 2) * 0.08}>
            <motion.article whileHover={{ y: -4 }} transition={{ type: "spring", stiffness: 300, damping: 24 }}
              data-testid={`key-service-${i}`}
              className="group flex h-full flex-col border border-white/10 bg-[var(--surface)] p-6 transition-colors duration-300 hover:border-[var(--red)]/50">
              <div className="mb-6 flex items-center justify-between">
                <it.icon className="text-[var(--red)]" size={24} />
                <span className="font-mono text-xs text-zinc-500">/0{i + 1}</span>
              </div>
              <h3 className="font-display text-2xl font-black uppercase leading-none md:text-3xl">{it.t}</h3>
              <p className="mt-3 text-sm leading-relaxed text-zinc-300">{it.d}</p>
            </motion.article>
          </Reveal>
        ))}
        <Reveal>
          <button onClick={() => scrollToId("kontakt")} data-testid="keys-cta-button"
            className="group flex h-full w-full items-center justify-between gap-4 bg-[var(--red)] p-6 text-left">
            <span className="font-display text-2xl font-black uppercase leading-none md:text-3xl">{t.cta}</span>
            <ArrowUpRight size={30} className="shrink-0 transition-transform duration-500 group-hover:rotate-45" />
          </button>
        </Reveal>
      </div>
    </div>
  </section>
  );
};
